import React from "react";
import ReactGA from "react-ga";
import mixpanel from "mixpanel-browser";

import { User } from "../types/UserTypes";

import * as EnvironmentUtils from "./EnvironmentUtils";
import * as Utils from "./Utils";
import * as AuthUtils from "./AuthUtils";
import * as Flags from "../Flags";
import * as ErrorUtils from "./ErrorUtils";
import * as FormatUtils from "./FormatUtils";

import * as UserSelectors from "../../store/User/User.selector";

import store from "../../store";

export type TrackingContextProps = {
	ga: string;
	mixpanel: any;
};

const mixpanelProjectID = EnvironmentUtils.getEnvironmentVariable(
	"REACT_APP_MIXPANEL_PROJECT_ID",
);
const mixpanelViewID = EnvironmentUtils.getEnvironmentVariable(
	"REACT_APP_MIXPANEL_VIEW_ID",
);
export const MIXPANEL_PROFILE_BASE_URL = `https://mixpanel.com/project/${mixpanelProjectID}/view/${mixpanelViewID}/app/profile`;

export const TrackingContext =
	React.createContext<Partial<string | TrackingContextProps>>("");

let initialized = false;
let gaInitialised = false;
let gaEnabled = false;

export const initialiseGA = (): void => {
	if (gaInitialised) {
		return;
	}

	gaInitialised = true;
	gaEnabled = true;

	const onScriptLoad = () => {
		const w: any = window;
		// Google tags
		w.dataLayer = w.dataLayer || [];
		w.dataLayer.push("js", new Date());
		w.dataLayer.push("config", "UA-119141064-5");

		const gaUid = EnvironmentUtils.getEnvironmentVariable("REACT_APP_GA_UID");

		if (gaUid) {
			ReactGA.initialize(gaUid, { debug: false, titleCase: false });
		}
	};

	// Load GA script
	const script: HTMLScriptElement = document.createElement("script");
	script.src = "https://www.googletagmanager.com/gtag/js?id=UA-119141064-5";
	script.defer = true;
	script.async = true;
	script.onload = onScriptLoad;
	document.head.appendChild(script);
};

export const blockGA = (): void => {
	gaEnabled = false;
};

export const initialise = (): void => {
	if (initialized) return;
	initialized = true;

	if (!isMixpanelEnabled()) {
		return;
	}

	const mixpanelToken = EnvironmentUtils.getEnvironmentVariable(
		"REACT_APP_MIXPANEL_TOKEN",
	);

	if (mixpanelToken) {
		mixpanel.init(mixpanelToken as string);
		identifyUser();
	}
};

export const userChange = (user: User | null): void => {
	initialise();
	const obj = user && {
		userId: user.uid,
	};
	ReactGA.set(obj || { userId: null });
	identifyUser(user);
};

export const identifyUser = (user?: User | null): void => {
	initialise();
	const userObject = user || AuthUtils.getStoredUserObject();

	if (!userObject || !isMixpanelEnabled()) {
		return;
	}

	// Identify user with uid
	mixpanel.identify(userObject.uid);

	// Register people properties
	updatePeopleProps({
		$first_name: userObject.firstName,
		$last_name: userObject.lastName,
		$email: userObject.email,
		Company: FormatUtils.getFleetDisplayName(userObject.fleetId),
		Group: userObject.group,
	});

	// Register version number to pass with tracking events
	mixpanel.register({
		"Portal Version": EnvironmentUtils.getAppInfo().version,
	});
};

export const updatePeopleProps = (updateData: { [key: string]: any }) => {
	mixpanel.people.set(updateData);
};

export const logError = (error: Error, fatal: boolean): void => {
	initialise();
	ReactGA.exception({
		identifier: ErrorUtils.parseError(error).identifier,
		description: error.toString(),
		fatal: fatal,
	});
};

export const trackPage = (): void => {
	initialise();

	if (gaEnabled) {
		ReactGA.pageview(window.location.pathname);
	}

	sendMixpanelEvent({
		name: "Page View",
	});
};

const isMixpanelEnabled = () => {
	return (
		EnvironmentUtils.getAppEnvironment() === "prod" ||
		Flags.ENABLE_MIXPANEL.is()
	);
};

export interface AnalyticsEvent {
	eventCategory: string;
	eventAction: string;
	eventLabel?: string;
	eventValue?: number;
	mixpanelProperties?: { name: string; [key: string]: any };
}

const sendMixpanelEvent = (
	mixpanelProperties: AnalyticsEvent["mixpanelProperties"],
) => {
	if (
		!mixpanelProperties ||
		!isMixpanelEnabled() ||
		!AuthUtils.isUserLoggedIn()
	) {
		return;
	}

	const { name, ...props } = mixpanelProperties;
	const state = store.getState();
	const fleetId = UserSelectors.selectCurrentScopeFleetId(state);
	const groupId = UserSelectors.selectCurrentScopeGroupId(state);

	mixpanel.track(name, {
		...props,
		"Window Height": window.innerHeight,
		"Window Width": window.innerWidth,
		"Viewed Fleet ID": fleetId,
		"Viewed Group ID": groupId,
	});
};

export const sendEvent = async (evt: AnalyticsEvent): Promise<void> => {
	const { mixpanelProperties, ...event } = evt;
	sendMixpanelEvent(mixpanelProperties);

	if (process.env.REACT_APP_DISABLE_TRACKING === "true") {
		if (process.env.REACT_APP_ENABLE_TRACKING_DEBUG === "true") {
			console.log("**Send Tracking Event**"); // eslint-disable-line no-console
			console.log(JSON.stringify(event)); // eslint-disable-line no-console
		}
		return;
	}

	if (gaEnabled) {
		const ga = await Utils.waitFor(() => window.ga, 20, false);
		ga("send", "event", event);
	}
};
